/*
* Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
*/
import React, {ChangeEvent, useCallback, useEffect, useState} from 'react';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import makeStyles from '@material-ui/core/styles/makeStyles';
import {translate} from '@ion/core/src/locale/locale';
import {isEmpty, processDelimiters} from '@ion/core/src/utils/StringUtil';
import GridClearFiltersButton from '@ion/dls/src/components/buttons/GridClearFiltersButton';
import {IconButton} from '@ion/dls/src/components/buttons/IconButton';
import Chip from '@ion/dls/src/components/chip/Chip';
import SearchField from '@ion/dls/src/components/search/SearchField';
import ChipsViewGrid from '@ion/dls/src/editors/grids/ChipsViewGrid';
import {MaterialExitCloseIcon} from '@ion/dls/src/icons/MaterialExitCloseIcon';
import TopNavFilterIcon from '@ion/dls/src/icons/TopNavFilterIcon';
import TopNavSearchIcon from '@ion/dls/src/icons/TopNavSearchIcon';
import Render from '@ion/dls/src/render/Render';
import {dlsColor, dlsTypography} from '@ion/dls/src/theme/theme';
import {DLSTheme, IconSize} from '@ion/dls/src/types/types';
import SearchScope, {SearchScopeValues} from '@ion/features/src/search/SearchScope';
import {isIdentifierLimitExceed, MAX_IDENTIFIER_SEARCH_LIMIT} from '@ion/features/src/search/utils';

export const useStyles = makeStyles((theme: DLSTheme) => {
  return {
    deleteIcon : {
      marginBottom : 3
    },
    endAdornment : {
      cursor               : 'pointer',
      display              : 'flex',
      alignItems           : 'center',
      marginRight          : 5,
      '& .MuiSvgIcon-root' : {
        width  : '0.65em',
        height : '0.65em'
      }
    },
    searchIcon : {
      padding        : '12px 0 12px 12px',
      display        : 'flex',
      justifyContent : 'center',
      alignItems     : 'center'
    },
    errorMessage : {
      ...dlsTypography('body3'),
      color       : dlsColor('caRed'),
      fontSize    : '12px !important',
      paddingLeft : '16px !important'
    },
    searchField : {
      '& .MuiTextField-root' : {
        width : '150%'
      }
    },
    paperClass : {
      left     : '368px !important',
      position : 'absolute !important',
      top      : '80% !important',
      zIndex   : 10
    }
  };
});

const searchFieldStyles = () => ({
  searchFieldRoot : {
    width        : 'fit-content',
    minWidth     : '85%',
    border       : 'none',
    marginBottom : 10
  }
});

interface JobDevicesSearchWidgetProps {
  isExact: boolean;
  setIsExact: Function;
  searchValue: string;
  searchedValue: string[];
  setSearchValue: Function;
  dropdownOpen?: boolean;
  setDropdownOpen?: Function;
  setSearchedValue: Function;
  preference?: string;
};

export const JobDevicesSearchWidget = ({
  isExact,
  setIsExact,
  preference,
  searchValue,
  dropdownOpen,
  searchedValue,
  setSearchValue,
  setDropdownOpen,
  setSearchedValue
}: JobDevicesSearchWidgetProps) => {
  const classes                         = useStyles();
  const [isValid, setIsValid]           = useState(true);
  const [anchorEl, setAnchorEl]         = useState(null);

  useEffect(() => {
    if (searchedValue?.length < 3) {
      setDropdownOpen(false);
    }
  }, [searchedValue]);

  function onSearchChange(e: ChangeEvent<{name?: string; value: string;}>) {
    const {value} = e.target;
    setSearchValue(value);
    setIsValid(!isIdentifierLimitExceed(value));
  };

  const onChange = useCallback(() => {
    if (!isEmpty(searchValue) && isValid) {
      const vals = processDelimiters(searchValue, {isExact: true, splitOnly: true, trim: true});
      setSearchedValue(prevState => {
        const newVals = [...prevState];
        newVals.push(...vals);
        return newVals;
      });
      setSearchValue('');
    }
  }, [isValid, searchValue]);

  const handleClick = function(e) {
    setAnchorEl(e.currentTarget);
    setDropdownOpen((prev) => !prev);
  };

  const onDelete = useCallback((ids) => {
    ids?.forEach(({id}) => {
      setSearchedValue(prevState => prevState?.filter((field) => !(field === id)));
    });
    setDropdownOpen(false);
  }, []);

  const columns = [
    {
      headerName               : translate('jobcreate:conflict.table.columns.identifier'),
      field                    : 'id',
      hide                     : false,
      sortable                 : false,
      checkboxSelection        : true,
      headerCheckboxSelection  : true,
      suppressHeaderMenuButton : true,
      width                    : 630
    }];
    
  const closeDropdown = useCallback(() => {
    setDropdownOpen(false);
  }, []);

  return (
    <>
      <ClickAwayListener onClickAway={closeDropdown}>
        <div className={classes.searchField}>
          <SearchField overrides={searchFieldStyles}
            placeholder={searchedValue?.length ? '' : translate('jobsearchengine:devicesSearchPlaceHolder')}
            onChange={onSearchChange}
            inputRef={anchorEl}
            aria-controls={dropdownOpen ? 'menu-list-grow' : undefined}
            aria-haspopup='true'
            optionsProps={{
              value                    : searchValue,
              detailedDeviceGridSearch : true,
              onKeyPress               : onChange
            }}
            startAdornment={<div className={classes.searchIcon}>
              <TopNavSearchIcon />
              <Render.If condition={searchedValue?.length} >
                {searchedValue.map((value, i) => {
                  if ((searchedValue?.length > 2) && (i > 1)) {
                    return;
                  }
                  return (<Chip key={`${i}-${value}`}
                    showCustomDeleteIcon={!(searchedValue?.length > 2)}
                    size='small'
                    maxWidth={false}
                    borderRadius='56px'
                    isCustomRoot
                    labelColor={dlsColor('grey2')}
                    hoverDefault
                    noTextTransform
                    classes={{
                      deleteIcon : classes.deleteIcon
                    }}
                    label={value}
                    customDeleteIcon={<MaterialExitCloseIcon size={IconSize.small} />}
                    onDelete={function() {
                      setSearchedValue(prevState => prevState?.filter((field) => !(field === value)));
                    }} />);
                }
                )}
              </Render.If>
              <Render.If condition={(searchedValue?.length > 2)}>
                <Chip key={'countChip'}
                  size='small'
                  maxWidth={false}
                  borderRadius='56px'
                  isCustomRoot
                  labelColor={dlsColor('grey2')}
                  hoverDefault
                  noTextTransform
                  label={`${(searchedValue?.length - 2)} More`} />
              </Render.If>
            </div>}
            endAdornment={
              <>
                <IconButton disabled={!(searchedValue?.length > 2)} onClick={handleClick} icon={<TopNavFilterIcon /> }/>
                <SearchScope queryAttribute={SearchScopeValues.JOBS}
                  pageName={SearchScopeValues.JOBS} setIsExact={setIsExact} isExact={isExact} />
                <GridClearFiltersButton areFiltersApplied
                  disabled={!searchedValue?.length}
                  onClearFilters={function() {
                    setSearchedValue([]);
                  }} />
              </>} />
          <ChipsViewGrid disablePortal
            open={dropdownOpen}
            customClass={classes.paperClass}
            columnDefs={columns}
            anchorE1={anchorEl}
            preference={preference}
            onDelete={onDelete}
            chipValues={searchedValue?.map((id) => { return {id: id}; })} />
        </div>
      </ClickAwayListener>
      <Render.If condition={!isValid}>
        <div className={classes.errorMessage}>
          {translate('common:search.globalSearchLimitError', {limit: MAX_IDENTIFIER_SEARCH_LIMIT})}
        </div>
      </Render.If>
    </>
  );
};

export default JobDevicesSearchWidget;
